import React, { useState, useRef } from 'react';
import api from '../services/api';
import { compressImage, resolveUploadUrl } from '../utils/helpers';
import { readClipboardImage, CLIPBOARD_MESSAGES } from '../utils/clipboardImage';
import ImageAnnotateModal from './ImageAnnotateModal';

// endpoint: where the image is POSTed (field name `file`, response { url }).
// Defaults to the KYC upload for the original callers.
// compressionOptions: preset passed to compressImage (on-device). Omit for the
// standard receipt/proof preset; pass DOCUMENT_COMPRESSION_OPTIONS for KYC/invoice.
const ImageUploadBox = ({ onUploadSuccess, currentUrl, label, required, helperText, endpoint = '/upload/kyc', compressionOptions }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [pasting, setPasting] = useState(false);
  // Built-in editor (2026-10-20): draw on the uploaded image, then it is
  // compressed and uploaded again like a fresh file (handleFile) and the new
  // URL replaces the old one through onUploadSuccess.
  const [drawing, setDrawing] = useState(false);
  const handleDrawClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || !currentUrl) return;
    setDrawing(true);
  };

  // "Paste" button: the tap equivalent of Ctrl/Cmd+V (phones have no shortcut).
  // Must call the clipboard synchronously inside the click — no awaits first.
  // stopPropagation: the box's own onClick opens the file picker.
  const handlePasteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || pasting) return;
    setError(null);
    setPasting(true);
    readClipboardImage().then(({ file, error: clipErr }) => {
      setPasting(false);
      if (clipErr) { setError(CLIPBOARD_MESSAGES[clipErr] || CLIPBOARD_MESSAGES.failed); return; }
      handleFile(file);
    });
  };

  const handleSelectClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const actionRow = (
    <div className="upload-box-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" className="btn btn-outline btn-small" onClick={handleSelectClick} disabled={uploading}>
        Select file
      </button>
      <button type="button" className="btn btn-outline btn-small" onClick={handlePasteClick} disabled={uploading || pasting} title="Paste the image you copied">
        {pasting ? 'Reading clipboard…' : '📋 Paste'}
      </button>
      {currentUrl && (
        <button type="button" className="btn btn-outline btn-small" onClick={handleDrawClick} disabled={uploading} title="Draw on this image (circle, arrow, note) before it is used">
          ✏️ Draw
        </button>
      )}
    </div>
  );

  const handleFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Compress on the user's DEVICE before uploading (never sends the raw
      // photo). Done first so an oversized phone photo is shrunk under the
      // server's 10MB limit rather than rejected. If compression fails for any
      // reason, fall back to the original so the upload still goes through.
      let uploadFile = file;
      try {
        const { file: compressed } = await compressImage(file, compressionOptions || {});
        uploadFile = compressed;
      } catch (compErr) {
        console.warn('Image compression failed; uploading original:', compErr);
      }

      if (uploadFile.size > 10 * 1024 * 1024) {
        setError('Image is too large even after compression (max 10MB)');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadFile);

      // Assuming your api service is set up to handle multipart/form-data
      // If not, you might need to adjust the headers, but axios usually handles it if you pass FormData
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.url) {
        onUploadSuccess(response.data.url);
      } else {
        throw new Error('No URL returned from server');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        // CONSUME the event: this box took the image. Without this the paste
        // bubbles up to any page/modal-level paste router (e.g. the platform
        // order form routes stray pastes to the TRADE screenshot) and the
        // same image lands in two places (operator report 2026-08-28).
        e.preventDefault();
        e.stopPropagation();
        handleFile(file);
        break;
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // tabIndex on the box: a click (or Tab) leaves keyboard focus ON the box, so
  // a Ctrl/Cmd+V afterwards targets THIS box, not the page-level paste router.
  return (
    <div className="form-group">
      {label && <label className="form-label">{label} {required && '*'}</label>}
      <div 
        className={`image-upload-box ${isDragging ? 'dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={() => fileInputRef.current?.click()}
        tabIndex={0}
        style={{
          border: `2px dashed ${isDragging ? '#4caf50' : '#ccc'}`,
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragging ? '#e8f5e9' : '#f9f9f9',
          position: 'relative',
          minHeight: '120px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'all 0.2s ease',
          outline: 'none'
        }}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          accept="image/*"
          onChange={handleFileChange}
        />

        {uploading ? (
          <div style={{ color: '#666' }}>
            <div className="spinner" style={{ margin: '0 auto 10px', width: '24px', height: '24px', border: '3px solid #ccc', borderTopColor: '#4caf50', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            Uploading...
          </div>
        ) : currentUrl ? (
          <div style={{ width: '100%', height: '100%' }}>
            <img 
              src={resolveUploadUrl(currentUrl)}
              alt="Uploaded"
              style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }}
            />
            <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666' }}>
              Choose a file, paste, or drop to change image
            </div>
            {actionRow}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '24px', marginBottom: '10px', color: '#aaa' }}>📷</div>
            <div style={{ color: '#555', fontWeight: '500' }}>Choose a file, paste, or drop image here</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '5px' }}>Supports JPG, PNG, WebP (Max 10MB)</div>
            {actionRow}
          </div>
        )}
      </div>
      
      {error && <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '5px' }}>{error}</div>}
      {helperText && <small className="form-text" style={{ marginTop: '5px', display: 'block' }}>{helperText}</small>}

      <ImageAnnotateModal
        isOpen={drawing}
        source={currentUrl ? resolveUploadUrl(currentUrl) : null}
        fileName={currentUrl ? String(currentUrl).split('/').pop() : 'image'}
        onCancel={() => setDrawing(false)}
        onSave={(file) => { setDrawing(false); handleFile(file); }}
      />

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .image-upload-box:focus { border-color: #2196f3 !important; }
      `}} />
    </div>
  );
};

export default ImageUploadBox;
